import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  output,
  signal,
} from '@angular/core'
import { NgControl } from '@angular/forms'
import { NewCustomFormControl } from '../../helpers/new-custom-nested-form'
import { ToggleOption } from '../../interfaces/toggle-option.interface'

/**
 * Form field picking one value out of a few options shown side by side.
 * Its value is the selected option's value. It behaves as a radio group:
 * one tab stop, the arrow keys move the selection.
 */
@Component({
  selector: 'wm-front-toggle',
  templateUrl: './toggle.component.html',
  styleUrls: ['./toggle.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class ToggleComponent extends NewCustomFormControl {
  /** The input's label */
  public label = input<string>()

  /** The options, in their display order */
  public options = input.required<ToggleOption[]>()

  /** The state enabled or not of the input */
  public disabled = input(false)

  /** Share the whole width between the options; otherwise as wide as their text */
  public fullWidth = input(false)

  /** Handle touch behaviour */
  public touched = output<void>()

  /** Disabled by the input or by the form control */
  public locked = computed(() => this.disabled() || this._formDisabled())

  public override ngControl: NgControl

  protected _formDisabled = signal(false)

  constructor() {
    const ngControl = inject(NgControl, { optional: true, self: true })
    super(ngControl as NgControl)
    this.ngControl = ngControl as NgControl
  }

  public override setDisabledState(isDisabled: boolean): void {
    super.setDisabledState(isDisabled)
    this._formDisabled.set(isDisabled)
  }

  public isSelected(option: ToggleOption): boolean {
    return option.value === this.value
  }

  /** The one option reachable with tab: the selected one, else the first enabled one */
  public isTabStop(option: ToggleOption): boolean {
    const selectable = this.options().filter((o) => !o.disabled)
    return (
      (selectable.find((o) => this.isSelected(o)) ?? selectable[0]) === option
    )
  }

  public select(option: ToggleOption): void {
    if (this.locked() || option.disabled || this.isSelected(option)) {
      return
    }
    this.setValue(option.value)
  }

  /** Selects the previous or next enabled option, wrapping around like a radio group */
  public move(event: Event, step: -1 | 1): void {
    event.preventDefault()
    const options = this.options()
    const selected = options.findIndex((option) => this.isSelected(option))
    // nothing selected yet: the right arrow goes to the first option, the left one to the last
    const start = selected === -1 && step === -1 ? 0 : selected
    for (let i = 1; i <= options.length; i++) {
      const index =
        (((start + step * i) % options.length) + options.length) %
        options.length
      const option = options[index]
      if (!option.disabled) {
        this.select(option)
        const buttons = (event.currentTarget as HTMLElement).querySelectorAll(
          'button'
        )
        buttons[index]?.focus()
        return
      }
    }
  }

  /** Emit on focus */
  public onFocus(): void {
    this.touched.emit()
  }
}
